import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Card,
  CardHeader,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Icon,
  Tooltip,
  Badge,
  Skeleton,
  Progress,
  SimpleGrid,
  Select,
  Button,
  ButtonGroup,
  useColorModeValue,
  usePrefersReducedMotion
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Circle, CircleMarker, Tooltip as LeafTooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, CartesianGrid } from "recharts";
import { FiTrendingUp, FiTrendingDown, FiBarChart, FiActivity, FiClock, FiRefreshCw, FiFileText } from "react-icons/fi";
import { useEffect, useState, useMemo, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import client from "../../../helpers/Client";
import AuthContext from "../../../contexts/Auth";
import ResellerSelect from "../../../components/select/ResellerSelect";
import MapFilters from "../../../components/map/MapFilters";
import urlToId from "../../../helpers/UrlToId";
import Link from "../../../components/Link";
import { ServiceState } from "../../../components/States";
import { translateType } from "../../../shared/libs/translate";

// Advanced Keyframes for premium animations
const float = keyframes`
  0% { transform: translateY(0px) rotate(0deg) scale(1); }
  25% { transform: translateY(-8px) rotate(1deg) scale(1.02); }
  50% { transform: translateY(-15px) rotate(0deg) scale(1.05); }
  75% { transform: translateY(-8px) rotate(-1deg) scale(1.02); }
  100% { transform: translateY(0px) rotate(0deg) scale(1); }
`;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(66, 153, 225, 0.4); }
  50% { transform: scale(1.08); opacity: 0.9; box-shadow: 0 0 0 10px rgba(66, 153, 225, 0); }
  100% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(66, 153, 225, 0); }
`;

const slideUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(30px) scale(0.95) rotateX(10deg); 
    filter: blur(5px);
  }
  to { 
    opacity: 1; 
    transform: translateY(0) scale(1) rotateX(0deg); 
    filter: blur(0px);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200px 0; transform: skewX(-20deg); }
  100% { background-position: calc(200px + 100%) 0; transform: skewX(-20deg); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(66, 153, 225, 0.2), 0 0 10px rgba(66, 153, 225, 0.2), 0 0 15px rgba(66, 153, 225, 0.2); }
  50% { box-shadow: 0 0 10px rgba(66, 153, 225, 0.4), 0 0 20px rgba(66, 153, 225, 0.4), 0 0 30px rgba(66, 153, 225, 0.4); }
  100% { box-shadow: 0 0 5px rgba(66, 153, 225, 0.2), 0 0 10px rgba(66, 153, 225, 0.2), 0 0 15px rgba(66, 153, 225, 0.2); }
`;

const rotate3D = keyframes`
  0% { transform: rotateY(0deg) rotateX(0deg); }
  25% { transform: rotateY(5deg) rotateX(2deg); }
  50% { transform: rotateY(0deg) rotateX(0deg); }
  75% { transform: rotateY(-5deg) rotateX(-2deg); }
  100% { transform: rotateY(0deg) rotateX(0deg); }
`;

const morphing = keyframes`
  0% { border-radius: 20px; transform: scale(1); }
  25% { border-radius: 30px; transform: scale(1.02); }
  50% { border-radius: 25px; transform: scale(1.05); }
  75% { border-radius: 35px; transform: scale(1.02); }
  100% { border-radius: 20px; transform: scale(1); }
`;

const sparkle = keyframes`
  0% { opacity: 0; transform: scale(0) rotate(0deg); }
  50% { opacity: 1; transform: scale(1) rotate(180deg); }
  100% { opacity: 0; transform: scale(0) rotate(360deg); }
`;

// Modern Stats Card Component
function ModernStatsCard({ title, value, change, changeType, icon, color, isLoading }) {
  const cardBg = "rgba(255, 255, 255, 0.95)";
  const borderColor = "gray.200";
  const textColor = "gray.600";
  const glowColor = `rgba(${color === 'blue' ? '66, 153, 225' : color === 'green' ? '72, 187, 120' : color === 'purple' ? '159, 122, 234' : '251, 211, 141'}, 0.3)`;

  return (
    <Card
      bg={cardBg}
      backdropFilter="blur(25px) saturate(180%)"
      border="2px solid"
      borderColor={borderColor}
      borderRadius="3xl"
      overflow="hidden"
      position="relative"
    /* Animations kaldırıldı */
    >
      {/* Multiple gradient overlays for depth */}
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bgGradient={`linear(135deg, ${color}.500 0%, ${color}.600 50%, ${color}.700 100%)`}
        opacity={0.05}
      />

      <Box
        position="absolute"
        top="-50%"
        right="-50%"
        w="120px"
        h="120px"
        bgGradient={`radial(circle, ${color}.400 0%, transparent 70%)`}
        opacity={0.15}
        filter="blur(20px)"
      />

      <CardBody p={8}>
        <VStack align="stretch" spacing={6}>
          <HStack justify="space-between">
            <Box
              p={3}
              borderRadius="2xl"
              bg={`${color}.50`}
              border="1px solid"
              borderColor={`${color}.200`}
              position="relative"
              overflow="hidden"
            >
              <Box
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                bgGradient={`linear(45deg, ${color}.100, ${color}.200)`}
                opacity={0.5}
              />
              <Icon
                as={icon}
                boxSize={10}
                color={`${color}.600`}
                position="relative"
                zIndex={1}
              />
            </Box>
            {change && (
              <Tooltip label={`${changeType === "increase" ? "Artış" : "Azalış"}: ${change}%`} hasArrow>
                <Badge
                  colorScheme={changeType === "increase" ? "green" : "red"}
                  variant="solid"
                  borderRadius="full"
                  px={4}
                  py={2}
                  fontSize="sm"
                  fontWeight="bold"
                  boxShadow={`0 4px 12px ${changeType === "increase" ? "rgba(72, 187, 120, 0.3)" : "rgba(245, 101, 101, 0.3)"}`}
                >
                  <Icon
                    as={changeType === "increase" ? FiTrendingUp : FiTrendingDown}
                    boxSize={4}
                    mr={1}
                  />
                  {change}%
                </Badge>
              </Tooltip>
            )}
          </HStack>

          <VStack align="stretch" spacing={3}>
            <Text
              fontSize="md"
              color={textColor}
              fontWeight="semibold"
              letterSpacing="wide"
              textTransform="uppercase"
            >
              {title}
            </Text>
            {isLoading ? (
              <Skeleton
                height="48px"
                borderRadius="xl"
                startColor={`${color}.100`}
                endColor={`${color}.200`}
              />
            ) : (
              <Text
                fontSize="4xl"
                fontWeight="900"
                color={`${color}.600`}
                lineHeight="1"
                bgGradient={`linear(135deg, ${color}.500, ${color}.700)`}
                bgClip="text"
              >
                {value}
              </Text>
            )}
          </VStack>
        </VStack>
      </CardBody>
    </Card>
  );
}

// Performance Chart Card
function PerformanceChart({ title, data, color }) {
  const cardBg = "rgba(255, 255, 255, 0.8)";
  const borderColor = "gray.200";
  const progressBg = "gray.100";

  return (
    <Card
      bg={cardBg}
      backdropFilter="blur(20px)"
      border="1px solid"
      borderColor={borderColor}
      borderRadius="2xl"
      overflow="hidden"
    /* Animations kaldırıldı */
    >
      <CardHeader pb={2}>
        <HStack justify="space-between">
          <Heading size="md">{title}</Heading>
          <Icon as={FiBarChart} color={`${color}.500`} />
        </HStack>
      </CardHeader>
      <CardBody pt={0}>
        <VStack spacing={4}>
          {data.map((item, index) => (
            <Box key={index} w="full">
              <HStack justify="space-between" mb={2}>
                <Text fontSize="sm">{item.label}</Text>
                <Text fontSize="sm" fontWeight="bold">
                  {item.value}%
                </Text>
              </HStack>
              <Progress
                value={item.value}
                colorScheme={color}
                size="md"
                borderRadius="full"
                bg={progressBg}
              />
            </Box>
          ))}
        </VStack>
      </CardBody>
    </Card>
  );
}

function NewTicketsCard({ items, isLoading, onMore, onOpen }) {
  const cardBg = "rgba(255, 255, 255, 0.95)";
  const borderColor = "gray.200";
  const textColor = "gray.700";
  return (
    <Card
      bg={cardBg}
      backdropFilter="blur(25px) saturate(180%)"
      border="2px solid"
      borderColor={borderColor}
      borderRadius="3xl"
      overflow="hidden"
      position="relative"
    >
      <CardHeader pb={2}>
        <HStack justify="space-between" align="center">
          <Heading size="md">Yeni Gelen İşler</Heading>
          <Button variant="outline" colorScheme="purple" size="sm" onClick={onMore} rightIcon={<Icon as={FiTrendingUp} />}>
            Tümünü Gör
          </Button>
        </HStack>
      </CardHeader>
      <CardBody pt={0}>
        <VStack align="stretch" spacing={3}>
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} height="56px" borderRadius="xl" startColor="purple.50" endColor="purple.100" />
            ))
          ) : (
            (items || []).map((t) => {
              const id = urlToId(t.url || "");
              const when = t.created_at ? new Date(t.created_at) : null;
              const whenStr = when ? when.toLocaleString("tr-TR", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
              return (
                <HStack
                  key={t.url || t.number || Math.random()}
                  justify="space-between"
                  align="center"
                  p={3}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="xl"
                  bg="white"
                  cursor="pointer"
                  _hover={{ bg: "purple.50", borderColor: "purple.200" }}
                  onClick={() => onOpen(id)}
                >
                  <VStack align="start" spacing={0}>
                    <HStack spacing={3}>
                      <Badge colorScheme="purple">#{t.number}</Badge>
                      <Text fontWeight="semibold" color={textColor} maxW={{ base: "220px", md: "300px" }} noOfLines={1}>
                        {t.title || t.full_name || t.customer_name || "Yeni iş"}
                      </Text>
                    </HStack>
                    <HStack spacing={2}>
                      {t.full_name ? <Badge colorScheme="gray">{t.full_name}</Badge> : null}
                      {t.city_info?.name ? <Badge colorScheme="gray">{t.city_info.name}</Badge> : null}
                    </HStack>
                  </VStack>
                  <VStack align="end" spacing={1}>
                    <Text fontSize="sm" color="gray.500">{whenStr}</Text>
                    {t.priority ? <Badge colorScheme={t.priority === "high" ? "red" : t.priority === "medium" ? "yellow" : "green"}>{t.priority}</Badge> : null}
                  </VStack>
                </HStack>
              );
            })
          )}
          {!isLoading && (!items || items.length === 0) ? (
            <Box p={3} border="1px dashed" borderColor="gray.200" borderRadius="xl" textAlign="center" color="gray.500">
              Kayıt bulunamadı
            </Box>
          ) : null}
        </VStack>
      </CardBody>
    </Card>
  );
}

export default function Home() {
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const auth = useContext(AuthContext);
  const prefersReducedMotion = usePrefersReducedMotion();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    tickets: 0,
    tasks: 0,
    customers: 0,
    employees: 0,
  });
  const [ticketStateStats, setTicketStateStats] = useState([
    { label: 'Yeni', value: 0 },
    { label: 'Devam Ediyor', value: 0 },
    { label: 'Tamamlandı', value: 0 }
  ]);
  const [heatmapCity, setHeatmapCity] = useState([]);
  const [heatmapProject, setHeatmapProject] = useState([]);
  const [weeklyClosures, setWeeklyClosures] = useState([]);
  const [avgCompletion, setAvgCompletion] = useState(null);
  const [waitReasons, setWaitReasons] = useState([]);
  const [srParts, setSrParts] = useState([]);
  const [srCostsMonthly, setSrCostsMonthly] = useState([]);
  const [srAvgDuration, setSrAvgDuration] = useState(null);
  const [serviceForms, setServiceForms] = useState([]);
  const [serviceFormsLoading, setServiceFormsLoading] = useState(false);
  const [serviceFormsError, setServiceFormsError] = useState(null);
  const [reseller, setReseller] = useState('');
  const [heatmapDistrict, setHeatmapDistrict] = useState([]);
  const [mapLevel, setMapLevel] = useState('city');
  const [mapMode, setMapMode] = useState('heat');
  const [homeTab, setHomeTab] = useState('genel');
  const [geoCity, setGeoCity] = useState([]);
  const [geoDistrict, setGeoDistrict] = useState([]);
  const [geoProject, setGeoProject] = useState([]);
  const [projectsPoints, setProjectsPoints] = useState([]);
  const [ticketsPoints, setTicketsPoints] = useState([]);
  const [mapZoom, setMapZoom] = useState(5);
  const [ticketStateFilter, setTicketStateFilter] = useState('all');
  const [mapFilters, setMapFilters] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [timeFilter, setTimeFilter] = useState('all');
  const cityNameCache = useRef(new Map());
  const [refreshTick, setRefreshTick] = useState(0);
  const [newTickets, setNewTickets] = useState([]);
  const [newTicketsLoading, setNewTicketsLoading] = useState(false);

  const filteredServiceForms = useMemo(() => {
    const items = Array.isArray(serviceForms) ? serviceForms : [];
    if (!reseller) return items;
    const resellerId = urlToId(reseller);
    if (!resellerId) return items;
    return items.filter((s) => {
      const rUrl = s?.related_reseller?.url;
      if (!rUrl) return false;
      return urlToId(rUrl) === resellerId;
    });
  }, [serviceForms, reseller]);

  const aggregatePoints = (points, precision = 3) => {
    const m = new Map();
    for (const p of points || []) {
      const k = `${Number(p.lat).toFixed(precision)}:${Number(p.lon).toFixed(precision)}`;
      const v = m.get(k);
      if (v) {
        v.count += 1;
      } else {
        m.set(k, { lat: Number(p.lat), lon: Number(p.lon), count: 1, name: p.name, state: p.state, city: p.city });
      }
    }
    return Array.from(m.values());
  };

  // Professional map data fetching with backend API integration
  const fetchMapData = async (filters = {}) => {
    try {
      setIsRefreshing(true);

      // Build query parameters
      const params = new URLSearchParams();

      if (filters.city) params.append('city', filters.city);
      if (ticketStateFilter !== 'all') params.append('state', ticketStateFilter);
      if (timeFilter !== 'all') {
        const now = new Date();
        let dateFrom, dateTo;

        switch (timeFilter) {
          case 'today':
            dateFrom = now.toISOString().split('T')[0];
            dateTo = dateFrom;
            break;
          case 'week':
            dateFrom = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            dateTo = now.toISOString().split('T')[0];
            break;
          case 'month':
            dateFrom = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
            dateTo = now.toISOString().split('T')[0];
            break;
          case 'year':
            dateFrom = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
            dateTo = now.toISOString().split('T')[0];
            break;
        }

        if (dateFrom) params.append('date_from', dateFrom);
        if (dateTo) params.append('date_to', dateTo);
      }
      if (reseller) params.append('reseller', urlToId(reseller));

      // Use the new professional backend endpoint
      const response = await client.get(`/tickets/map-data/?${params.toString()}`);

      if (response.data?.success) {
        const { map_points, city_statistics, neighborhood_heatmap } = response.data;

        // Transform backend data to frontend format
        const transformedPoints = map_points.map(point => ({
          lat: point.latitude,
          lon: point.longitude,
          name: `#${point.number}`,
          state: point.state,
          city: point.city,
          count: 1, // Individual tickets
          id: point.id
        }));

        setTicketsPoints(transformedPoints);

        // You can also process city_statistics and neighborhood_heatmap for additional features
        console.log('City Statistics:', city_statistics);
        console.log('Neighborhood Heatmap:', neighborhood_heatmap);

        return response.data;
      }

      return null;
    } catch (error) {
      console.error('Error fetching map data:', error);
      return null;
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handle time filter changes
  const handleTimeFilterChange = (newTimeFilter) => {
    setTimeFilter(newTimeFilter);

    // Calculate date range based on time filter
    const now = new Date();
    let dateFrom, dateTo;

    switch (newTimeFilter) {
      case 'today':
        dateFrom = now.toISOString().split('T')[0];
        dateTo = dateFrom;
        break;
      case 'week':
        dateFrom = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        dateTo = now.toISOString().split('T')[0];
        break;
      case 'month':
        dateFrom = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        dateTo = now.toISOString().split('T')[0];
        break;
      case 'year':
        dateFrom = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
        dateTo = now.toISOString().split('T')[0];
        break;
      default:
        dateFrom = '';
        dateTo = '';
    }

    // Update map filters
    const newFilters = {
      ...mapFilters,
      dateFrom,
      dateTo
    };
    // Remove empty date filters
    if (!dateFrom) delete newFilters.dateFrom;
    if (!dateTo) delete newFilters.dateTo;
    setMapFilters(newFilters);
    fetchMapData(newFilters);
  };
  // Handle map filter changes
  const handleMapFiltersChange = (filters) => {
    setMapFilters(filters);
    fetchMapData(filters);
  };
  const radiusByCount = (c) => Math.max(4, Math.min(14, 4 + Math.log2((c || 1) + 1) * 3));
  const precisionForAgg = (z) => (z <= 6 ? 2 : z <= 8 ? 3 : z <= 10 ? 4 : 5);
  const hashString = (s) => {
    if (!s) return 0;
    let h = 0;
    for (let i = 0; i < s.length; i++) {
      h = Math.imul(31, h) + s.charCodeAt(i);
    }
    return (h >>> 0);
  };
  const jitterProjectPoint = (p, z) => {
    const seed = String(p.id || p.name || `${p.lat}:${p.lon}`);
    const h = hashString(seed);
    const angle = (h % 360) * Math.PI / 180;
    const amplitude = Math.max(0.00002, Math.min(0.001, 0.00012 * (12 / Math.max(z, 1))));
    const lat = p.lat + amplitude * Math.cos(angle);
    const lon = p.lon + amplitude * Math.sin(angle);
    return { ...p, lat, lon };
  };
  const ticketsAgg = useMemo(() => aggregatePoints(ticketsPoints, precisionForAgg(mapZoom)), [ticketsPoints, mapZoom]);
  const projectsAgg = useMemo(() => {
    const jittered = projectsPoints.map((p) => jitterProjectPoint(p, mapZoom));
    return aggregatePoints(jittered, precisionForAgg(mapZoom));
  }, [projectsPoints, mapZoom]);

  useEffect(() => {
    let mounted = true;
    async function fetchStats() {
      setIsLoading(true);
      setError(null);
      try {
        const resellerParam = reseller ? { reseller: urlToId(reseller) } : {};
        const requests = [
          client.get('/tickets/summary/', { params: { ...resellerParam } }),
          client.get('/tickets/stats/', { params: { group_by: 'city', ...resellerParam } }),
          client.get('/tickets/stats/', { params: { group_by: 'project', ...resellerParam } }),
          client.get('/tickets/stats/', { params: { period: 'week', metric: 'closures', ...resellerParam } }),
          client.get('/tickets/stats/', { params: { metric: 'avg_completion_time', ...resellerParam } }),
          client.get('/tickets/stats/', { params: { metric: 'wait_reasons', ...resellerParam } }),
          client.get('/service_records/stats/', { params: { metric: 'parts_replaced', ...resellerParam } }),
          client.get('/service_records/stats/', { params: { metric: 'costs_monthly', ...resellerParam } }),
          client.get('/service_records/stats/', { params: { metric: 'avg_duration', ...resellerParam } }),
        ];

        const results = await Promise.allSettled(requests);
        const [summaryRes, hmCityRes, hmProjRes, weeklyRes, avgCompRes, waitRes, partsRes, costsRes, avgDurRes] = results;

        const extractCount = (res) => {
          if (res?.status === 'fulfilled') {
            const data = res.value?.data;
            if (!data) return 0;
            // Prefer EasyPagination format
            if (typeof data?.num_of_items === 'number') return data.num_of_items;
            // Fallback if some endpoints still return count
            if (typeof data?.count === 'number') return data.count;
            // As a last resort, try array or results length
            if (Array.isArray(data)) return data.length;
            if (Array.isArray(data?.results)) return data.results.length;
          }
          return 0;
        };

        const sum = summaryRes?.status === 'fulfilled' ? (summaryRes.value?.data || {}) : {};
        const nextStats = {
          tickets: Number(sum?.tickets_total || 0),
          tasks: Number(sum?.tasks_total || 0),
          customers: Number(sum?.customers_total || 0),
          employees: Number(sum?.employees_total || 0),
        };
        const states = [
          { label: 'Yeni', value: Number(sum?.states?.assignment || 0) },
          { label: 'Devam Ediyor', value: Number(sum?.states?.in_progress || 0) },
          { label: 'Tamamlandı', value: Number(sum?.states?.completed || 0) },
        ];

        if (mounted) {
          setStats(nextStats);
          setTicketStateStats(states);
          if (hmCityRes?.status === 'fulfilled') setHeatmapCity(Array.isArray(hmCityRes.value?.data) ? hmCityRes.value.data : []);
          if (hmProjRes?.status === 'fulfilled') setHeatmapProject(Array.isArray(hmProjRes.value?.data) ? hmProjRes.value.data : []);
          if (weeklyRes?.status === 'fulfilled') setWeeklyClosures(Array.isArray(weeklyRes.value?.data) ? weeklyRes.value.data.map(d => ({ label: new Date(d.week).toLocaleDateString('tr-TR'), count: d.count })) : []);
          if (avgCompRes?.status === 'fulfilled') setAvgCompletion(typeof avgCompRes.value?.data?.avg_seconds === 'number' ? avgCompRes.value.data.avg_seconds : null);
          if (waitRes?.status === 'fulfilled') setWaitReasons(Array.isArray(waitRes.value?.data) ? waitRes.value.data : []);
          if (partsRes?.status === 'fulfilled') setSrParts(Array.isArray(partsRes.value?.data) ? partsRes.value.data : []);
          if (costsRes?.status === 'fulfilled') setSrCostsMonthly(Array.isArray(costsRes.value?.data) ? costsRes.value.data.map(d => ({ label: new Date(d.month).toLocaleDateString('tr-TR', { year: 'numeric', month: 'short' }), total: Number(d.total) })) : []);
          if (avgDurRes?.status === 'fulfilled') setSrAvgDuration(typeof avgDurRes.value?.data?.avg_seconds === 'number' ? avgDurRes.value.data.avg_seconds : null);
        }
      } catch (err) {
        if (mounted) setError(err);
      } finally {
        if (mounted) setIsLoading(false);
      }
    }
    fetchStats();
    return () => { mounted = false; };
  }, [refreshTick, reseller]);

  useEffect(() => {
    let mounted = true;
    async function run() {
      try {
        setNewTicketsLoading(true);
        const params = { state: "assignment", ordering: "-created_at", page_size: 6 };
        if (reseller) params.assigned_to = urlToId(reseller);
        const resp = await client.get('/tickets/', { params, disableCache: true });
        const data = resp?.data;
        let items = [];
        if (Array.isArray(data)) items = data;
        else if (Array.isArray(data?.results)) items = data.results;
        else if (Array.isArray(data?.data)) items = data.data;
        if (mounted) setNewTickets(items);
      } catch (_) {
        if (mounted) setNewTickets([]);
      } finally {
        if (mounted) setNewTicketsLoading(false);
      }
    }
    run();
    return () => { mounted = false; };
  }, [reseller, refreshTick]);

  useEffect(() => {
    let mounted = true;
    async function fetchServiceForms() {
      setServiceFormsLoading(true);
      setServiceFormsError(null);
      try {
        const resp = await client.get('/service_records/', {
          params: { page: 1, page_size: 50 }
        });
        const data = resp.data;
        let items = [];
        if (Array.isArray(data)) {
          items = data;
        } else if (Array.isArray(data?.results)) {
          items = data.results;
        } else if (Array.isArray(data?.data)) {
          items = data.data;
        }
        if (mounted) {
          setServiceForms(items);
        }
      } catch (err) {
        if (mounted) setServiceFormsError(err);
      } finally {
        if (mounted) setServiceFormsLoading(false);
      }
    }
    fetchServiceForms();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    async function fetchAnalytics() {
      try {
        const resellerParam = reseller ? { reseller: urlToId(reseller) } : {};
        const analyticsReqs = [
          client.get('/tickets/stats/', { params: { group_by: 'city', ...resellerParam } }),
          client.get('/tickets/stats/', { params: { group_by: 'district', ...resellerParam } }),
          client.get('/tickets/stats/', { params: { group_by: 'project', ...resellerParam } }),
          client.get('/tickets/stats/', { params: { period: 'week', metric: 'closures', ...resellerParam } }),
          client.get('/tickets/stats/', { params: { metric: 'avg_completion_time', ...resellerParam } }),
          client.get('/tickets/stats/', { params: { metric: 'wait_reasons', ...resellerParam } }),
          client.get('/service_records/stats/', { params: { metric: 'parts_replaced', ...resellerParam } }),
          client.get('/service_records/stats/', { params: { metric: 'costs_monthly', ...resellerParam } }),
          client.get('/service_records/stats/', { params: { metric: 'avg_duration', ...resellerParam } }),
        ];
        const results = await Promise.allSettled(analyticsReqs);
        const [hmCityRes, hmDistRes, hmProjRes, weeklyRes, avgCompRes, waitRes, partsRes, costsRes, avgDurRes] = results;
        if (mounted) {
          if (hmCityRes?.status === 'fulfilled') setHeatmapCity(Array.isArray(hmCityRes.value?.data) ? hmCityRes.value.data : []);
          if (hmDistRes?.status === 'fulfilled') setHeatmapDistrict(Array.isArray(hmDistRes.value?.data) ? hmDistRes.value.data : []);
          if (hmProjRes?.status === 'fulfilled') setHeatmapProject(Array.isArray(hmProjRes.value?.data) ? hmProjRes.value.data : []);
          if (weeklyRes?.status === 'fulfilled') setWeeklyClosures(Array.isArray(weeklyRes.value?.data) ? weeklyRes.value.data.map(d => ({ label: new Date(d.week).toLocaleDateString('tr-TR'), count: d.count })) : []);
          if (avgCompRes?.status === 'fulfilled') setAvgCompletion(typeof avgCompRes.value?.data?.avg_seconds === 'number' ? avgCompRes.value.data.avg_seconds : null);
          if (waitRes?.status === 'fulfilled') setWaitReasons(Array.isArray(waitRes.value?.data) ? waitRes.value.data : []);
          if (partsRes?.status === 'fulfilled') setSrParts(Array.isArray(partsRes.value?.data) ? partsRes.value.data : []);
          if (costsRes?.status === 'fulfilled') setSrCostsMonthly(Array.isArray(costsRes.value?.data) ? costsRes.value.data.map(d => ({ label: new Date(d.month).toLocaleDateString('tr-TR', { year: 'numeric', month: 'short' }), total: Number(d.total) })) : []);
          if (avgDurRes?.status === 'fulfilled') setSrAvgDuration(typeof avgDurRes.value?.data?.avg_seconds === 'number' ? avgDurRes.value.data.avg_seconds : null);
        }
      } catch (_) { }
    }
    fetchAnalytics();
    return () => { mounted = false; };
  }, [refreshTick, reseller]);

  // Projects points fetching disabled to avoid heavy frontend queries
  useEffect(() => {
    setProjectsPoints([]);
  }, [reseller]);

  // Tickets points use backend map-data endpoint; refresh on filters
  useEffect(() => {
    const filters = { ...mapFilters };
    fetchMapData(filters);
  }, [reseller, timeFilter, ticketStateFilter, refreshTick]);

  // WebSocket: refresh dashboard stats and map on backend updates
  useEffect(() => {
    try {
      const base = (process.env.REACT_APP_API_BASE_URL || window.location.origin).replace(/^http/, 'ws').replace(/\/$/, '');
      let resellerId = undefined;
      try {
        const user = auth?.data?.user;
        const resellerUrl = user?.employee?.reseller?.url;
        resellerId = reseller ? urlToId(reseller) : (resellerUrl ? urlToId(resellerUrl) : undefined);
      } catch (_) { }
      const wsUrl = base + "/ws/dashboard/" + (resellerId ? `?reseller=${resellerId}` : "");
      const ws = new WebSocket(wsUrl);
      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data?.event === 'resource') {
            if (!data?.reseller || data.reseller === 'all' || (resellerId && String(data.reseller) === String(resellerId))) {
              setRefreshTick(Date.now());
            }
          }
        } catch (_) { }
      };
      return () => { try { ws.close(); } catch (_) { } };
    } catch (_) { }
  }, [auth, reseller]);

  const totalTickets = useMemo(() => ticketStateStats.reduce((sum, s) => sum + (s.value || 0), 0), [ticketStateStats]);
  const chartData = useMemo(() => {
    const total = totalTickets || 1;
    return ticketStateStats.map(s => ({ label: s.label, value: Math.round((s.value / total) * 100) }));
  }, [ticketStateStats, totalTickets]);

  const formatDurationMinutes = (value) => {
    if (!value) return null;
    if (typeof value === 'number') return Math.round(value / 60);
    const s = String(value);
    const dayMatch = s.match(/(\d+)\s*day/);
    const days = dayMatch ? parseInt(dayMatch[1], 10) : 0;
    const hms = s.split(',').pop().trim();
    const parts = hms.split(':').map(p => parseFloat(p));
    if (parts.length >= 2) {
      const [h, m, sec] = parts;
      const minutes = days * 24 * 60 + (h || 0) * 60 + (m || 0) + ((sec || 0) / 60);
      return Math.round(minutes);
    }
    return null;
  };

  const delay = (ms) => new Promise((r) => setTimeout(r, ms));
  const geocode = async (query) => {
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=tr&limit=1`;
    try {
      await delay(250);
      const resp = await fetch(url);
      if (!resp.ok) return null;
      const data = await resp.json();
      const first = Array.isArray(data) ? data[0] : null;
      if (first) return first;
    } catch { }
    return null;
  };

  const fetchAllPages = async (path, params, pageSize = 100, maxPages = 5) => {
    let page = 1;
    let all = [];
    for (let i = 0; i < maxPages; i++) {
      const res = await client.get(path, { params: { ...params, page_size: pageSize, page } });
      const list = Array.isArray(res.data?.results) ? res.data.results : Array.isArray(res.data) ? res.data : [];
      all = all.concat(list);
      const next = res.data?.next;
      if (next) {
        page += 1;
        continue;
      }
      if (list.length < pageSize) break;
      break;
    }
    return all;
  };

  useEffect(() => {
    let mounted = true;
    async function run() {
      const items = heatmapCity.slice(0, 50);
      const results = [];
      for (const i of items) {
        const q = `${i.name}, Türkiye`;
        const p = await geocode(q);
        if (p) results.push({ ...i, lat: parseFloat(p.lat), lon: parseFloat(p.lon) });
        if (!mounted) break;
      }
      if (mounted) setGeoCity(results);
    }
    run();
    return () => { mounted = false; };
  }, [heatmapCity]);

  useEffect(() => {
    let mounted = true;
    async function run() {
      const items = heatmapDistrict.slice(0, 50);
      const results = [];
      for (const i of items) {
        const q = `${i.name}, ${i.city}, Türkiye`;
        const p = await geocode(q);
        if (p) results.push({ ...i, lat: parseFloat(p.lat), lon: parseFloat(p.lon) });
        if (!mounted) break;
      }
      if (mounted) setGeoDistrict(results);
    }
    run();
    return () => { mounted = false; };
  }, [heatmapDistrict]);

  useEffect(() => {
    let mounted = true;
    async function run() {
      const items = heatmapProject.slice(0, 50);
      const results = [];
      for (const p of items) {
        try {
          const res = await client.get(`/projects/${p.key}/`);
          const pr = res.data || {};
          const cityName = pr?.city_info?.name || p.city || '';
          const q = `${pr?.address || p.name}, ${cityName}, Türkiye`;
          let g = null;
          if (q.trim()) g = await geocode(q);
          if (!g && cityName) {
            const cg = await geocode(`${cityName}, Türkiye`);
            if (cg) g = cg;
          }
          if (g) results.push({ ...p, lat: parseFloat(g.lat), lon: parseFloat(g.lon), address: pr?.address || '' });
          if (!mounted) break;
        } catch { }
      }
      if (mounted) setGeoProject(results);
    }
    run();
    return () => { mounted = false; };
  }, [heatmapProject]);

  return (
    <Box minH="100vh" bg={bgColor} position="relative" overflow="hidden">
      <Container maxW="container.2xl" py={8}>
        <VStack spacing={8} align="stretch">
          <Box
            bgGradient={useColorModeValue('linear(135deg, var(--chakra-colors-primary-50), var(--chakra-colors-accent-50))', 'linear(135deg, var(--chakra-colors-gray-800), var(--chakra-colors-gray-700))')}
            border="1px solid"
            borderColor={useColorModeValue('gray.200', 'gray.700')}
            borderRadius="xl"
            p={{ base: 4, md: 6 }}
          >
            <HStack justify="space-between" align="start" flexWrap="wrap" gap={4}>
              <VStack align="start" spacing={1}>
                <Heading size="lg" bgClip="text" bgGradient={useColorModeValue('linear(135deg, var(--chakra-colors-primary-600), var(--chakra-colors-accent-600))', 'linear(135deg, var(--chakra-colors-primary-300), var(--chakra-colors-accent-300))')}>
                  Ana Sayfa
                </Heading>
                <Text color={useColorModeValue('gray.600', 'gray.300')}>Gerçek zamanlı özet ve hızlı erişim</Text>
              </VStack>
            </HStack>
          </Box>

          <HStack justify="flex-end">
            <ResellerSelect reseller={reseller} setReseller={setReseller} bg={'white'} width={{ base: '100%', md: '280px' }} />
          </HStack>

          {error && (
            <Card border="1px solid" borderColor="red.200">
              <CardBody>
                <Text color="red.600">Veriler yüklenirken bir hata oluştu.</Text>
              </CardBody>
            </Card>
          )}

          <Box display={homeTab === 'genel' ? 'block' : 'none'}>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              <ModernStatsCard
                title="Toplam İş Emri"
                value={stats.tickets}

                changeType={"increase"}
                icon={FiBarChart}
                color="blue"
                isLoading={isLoading}
              />
              <ModernStatsCard
                title="Görev Sayısı"
                value={stats.tasks}
                changeType={"increase"}
                icon={FiActivity}
                color="purple"
                isLoading={isLoading}
              />
              <ModernStatsCard
                title="Müşteri Sayısı"
                value={stats.customers}
                changeType={"increase"}
                icon={FiTrendingUp}
                color="green"
                isLoading={isLoading}
              />

            </SimpleGrid>
          </Box>

          <Box display={homeTab === 'genel' ? 'block' : 'none'}>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
              <NewTicketsCard
                items={newTickets}
                isLoading={newTicketsLoading}
                onMore={() => navigate('/is-emri/?state=assignment')}
                onOpen={(id) => navigate(`/is-emri/${id}`)}
              />
              <ModernStatsCard
                title="Toplam İş Emirleri"
                value={stats.tickets}
                changeType={"increase"}
                icon={FiBarChart}
                color="blue"
                isLoading={isLoading}
              />
              <ModernStatsCard
                title="Bitirilen İş Emirleri"
                value={(ticketStateStats.find(s => s.label === 'Tamamlandı')?.value) || 0}
                changeType={"increase"}
                icon={FiTrendingUp}
                color="green"
                isLoading={isLoading}
              />
              <ModernStatsCard
                title="Bekleyen İş Emirleri"
                value={(ticketStateStats.find(s => s.label === 'Devam Ediyor')?.value) || 0}
                changeType={"increase"}
                icon={FiClock}
                color="yellow"
                isLoading={isLoading}
              />
            </SimpleGrid>
          </Box>

          <Box display={homeTab === 'genel' ? 'block' : 'none'}>
            <SimpleGrid columns={{ base: 1, lg: 1 }} spacing={6}>

              <Card bg="rgba(255,255,255,0.8)" border="1px solid" borderColor="gray.200" borderRadius="2xl">
                <CardHeader pb={2}>
                  <Heading size="md">Özet</Heading>
                </CardHeader>
                <CardBody pt={0}>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text color="gray.600">Toplam İş Emri</Text>
                      <Badge colorScheme="blue">{stats.tickets}</Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Devam Eden</Text>
                      <Badge colorScheme="yellow">{ticketStateStats[1]?.value || 0}</Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Tamamlanan</Text>
                      <Badge colorScheme="green">{ticketStateStats[2]?.value || 0}</Badge>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            </SimpleGrid>

          </Box>

        </VStack>
      </Container>
    </Box>
  );
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || !points.length) return;
    const latlngs = points.map(p => [p.lat, p.lon]);
    try {
      const bounds = L.latLngBounds(latlngs);
      map.fitBounds(bounds, { padding: [20, 20] });
    } catch { }
  }, [points, map]);
  return null;
}

function ZoomWatcher({ onZoom }) {
  const map = useMap();
  useEffect(() => {
    try { onZoom(map.getZoom()); } catch { }
    const handler = () => {
      try { onZoom(map.getZoom()); } catch { }
    };
    map.on('zoomend', handler);
    return () => {
      map.off('zoomend', handler);
    };
  }, [map, onZoom]);
  return null;
}
